import { Select, TextAreaInput, TextInput } from '@octano/global-ui';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { Col, Row } from 'reactstrap';
import useTextValidationType from '../../../../components/curriculum/hooks/useTextValidationType';
import useValidatorNotes from '../../../../hooks/useValidatorNotes';
import { CreateValidationFields } from '../../types';
import { SecureResponseFile } from '../../../../components/AzureFilesUploader/types';
import AzureFilesUploader from '../../../../components/AzureFilesUploader/AzureFilesUploader';
import ValidationFileChip from './ValidationFileChip';
import { useValidations } from '../../../../hooks/useValidations';
import { Institution } from '../../../Configurations/Institutions/interfaces/type';
import TooltipForSelect from '../../../../components/tooltip/TooltipForSelect';

interface ExternalValidationFormProps {
  readonly?: boolean;
  institutions?: Institution[];
}

const ExternalValidationForm = ({
  readonly = false,
  institutions = [],
}: ExternalValidationFormProps) => {
  const { texts } = useTextValidationType();
  const { formattertNote, formattertExternalNote } = useValidatorNotes();
  const { msgValidations } = useValidations();

  const { control, watch } = useFormContext<CreateValidationFields>();
  const supplementaryFiles = useFieldArray({
    control,
    name: 'validationSupplementaryFilesIds',
  });

  const [files] = watch(['validationSupplementaryFilesIds']);

  const [filesState, setFilesState] = useState(0);
  const handleAddFile = useCallback(
    (file: SecureResponseFile) => {
      supplementaryFiles.append(file);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );

  const handleDownloadFile = useCallback((file: SecureResponseFile) => {
    if (!file?.url) {
      return;
    }

    window.open(file?.url, '_blank');
  }, []);

  const handleRemoveFile = useCallback(
    (index: number) => {
      supplementaryFiles.remove(index);
      setFilesState((prev) => prev + 1);
    },
    [supplementaryFiles],
  );

  return (
    <Row className="mb-4">
      <Col xs={12} md={6}>
        <TextInput
          name="external.externalCourseName"
          label={texts.externalCourseName}
          control={control}
          disabled={readonly}
          rules={{
            required: texts.required,
          }}
        />
      </Col>
      <Col xs={12} md={6}>
        <Select
          name="external.institutionId"
          label={texts.institutionLabel}
          options={institutions.map((e) => ({
            label: e.name,
            value: e.id,
          }))}
          control={control}
          disabled={readonly}
          rules={{ required: texts.required }}
        />
      </Col>
      <Col xs={12} md={6}>
        <TooltipForSelect
          id="courseGrade"
          text={texts.externalCourseGradeTooltip}
          hasValue
          isClearable
          marginRight={20}
        >
          <TextInput
            name="external.courseGrade"
            label={texts.courseGradeLabel}
            control={control}
            disabled={readonly}
            rules={{
              required: texts.required,
              validate: (value: any) => {
                if (!value) {
                  return false;
                }
                return formattertNote(value) !== '' || texts.invalidGrade;
              },
            }}
          />
        </TooltipForSelect>
      </Col>

      <Col xs={12} md={6}>
        <TooltipForSelect
          id="externalGrade"
          text={texts.externalExternalGradeTooltip}
          hasValue
          isClearable
          marginRight={20}
        >
          <TextInput
            name="external.externalGrade"
            label={texts.externalGradeLabel}
            control={control}
            disabled={readonly}
            rules={{
              required: texts.required,
              validate: (value: any) => {
                if (!value) {
                  return false;
                }
                return (
                  formattertExternalNote(value) !== '' || texts.invalidGrade
                );
              },
            }}
          />
        </TooltipForSelect>
      </Col>
      <Col xs={12}>
        <TextAreaInput
          label={texts.description.label}
          name="external.description"
          placeholder={texts.description.placeholder}
          disabled={readonly}
          rules={{
            maxLength: msgValidations.invalidMaxLength(500),
          }}
          control={control}
        />
      </Col>
      {!!(!readonly || files?.length) && (
        <Col className="mt-4" xs={12}>
          <div
            className="w-full p-4 rounded"
            style={{
              boxShadow: '0px 0px 10px 2px rgba(17,17,26,0.08)',
            }}
            key={filesState.toString()}
          >
            <AzureFilesUploader
              label={texts.uploadFile.label}
              disabled={readonly}
              onUploaded={(next) => handleAddFile(next)}
            />
            {!!files?.length && (
              <div
                className="w-full mt-3 d-flex flex-wrap align-items-center justify-content-start"
                style={{ gap: 5 }}
              >
                {files?.map((file, i) => (
                  <ValidationFileChip
                    key={file.id.toString()}
                    value={i}
                    children={`${file.name}.${file.extension}`}
                    onRemove={!readonly ? handleRemoveFile : undefined}
                    onDownload={() => handleDownloadFile(file)}
                  />
                ))}
              </div>
            )}
          </div>
        </Col>
      )}
    </Row>
  );
};

export default ExternalValidationForm;
